<script lang="ts">
  import { onDestroy, tick } from "svelte"
  import { closeOverlayById, openOverlay } from "@kontextwerk/web-sdk/core/overlay/globalStores"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import {
    ApiError,
    type OrganizationPresentation,
  } from "../../../../../../api/public/presentation"
  import type { OrgPresentationMode } from "../../../../_shared/_logic/presentation-mode"
  import {
    bookAppointmentSlot,
    cancelAppointment,
    confirmAppointment,
    supplementAppointmentContactDetails,
    type PublicAppointmentContactCompletion,
    type PublicAppointmentContactSupplement,
    type PublicAppointmentPortalMutationResponse,
    fetchAppointmentPortalState,
    reportWrongRecipient,
    type PublicAppointmentParticipantInput,
    type PublicAppointmentPortalStateResponse,
    type PublicAppointmentSummary,
    type PublicAppointmentSlot,
    type PublicAppointmentWrongRecipientRequest,
  } from "../../../../../../api/public/appointment-portal"
  import AppointmentActionDetailsOverlay from "./_modules/appointment-action-details-overlay"
  import AppointmentStatusPanel from "./_modules/appointment-status-panel"
  import CodeGate from "./_modules/code-gate"
  import SlotRecommendationView from "./_modules/slot-recommendation-view"
  import SlotSelectionView from "./_modules/slot-selection-view"
  import WrongRecipientReportOverlay from "./_modules/wrong-recipient-report-overlay"
  import type { AppointmentPortalContactMethodHints } from "../../../../_shared/_logic/contact-method-hints"
  import { normalizePortalCode } from "./_shared/_logic/appointment-portal"
  import { resolveSlotBookingConflictMessage } from "./_logic/bookingErrors"
  import { ContactCompletionDialog } from "./_modules/contact-completion"

  let {
    orgIdentifier,
    organization,
    initialCode = null,
    presentationMode = "appointment_portal_only",
    contactMethodHints = null,
    oncodeResolved = null,
    onContactMethodSubmitted = null,
  }: {
    orgIdentifier: string
    organization: OrganizationPresentation | null
    initialCode?: string | null
    presentationMode?: OrgPresentationMode
    contactMethodHints?: AppointmentPortalContactMethodHints | null
    oncodeResolved?: ((code: string | null) => void) | null
    onContactMethodSubmitted?: ((participant: PublicAppointmentParticipantInput) => void) | null
  } = $props()

  let code = $state<string | null>(null)
  let loadedCode = $state<string | null>(null)
  let portalState = $state<PublicAppointmentPortalStateResponse | null>(null)
  let loading = $state(false)
  let busy = $state(false)
  let error = $state<string | null>(null)
  type PortalView = "status" | "recommendations" | "calendar"

  let portalView = $state<PortalView>("status")
  let slotViewRevision = $state(0)
  let successMessage = $state<string | null>(null)
  let handledAppointmentAction = $state<string | null>(null)
  type ContactCompletionPrompt = {
    orgIdentifier: string
    code: string
    appointmentId: string
    participant: PublicAppointmentParticipantInput
    availability: PublicAppointmentContactCompletion
  }
  let contactCompletionPrompt = $state<ContactCompletionPrompt | null>(null)
  let contactCompletionOverlayId: string | null = null
  let completionRevision = 0
  let destroyed = false
  const offeredContactCompletions = new Set<string>()

  const effectiveOrgIdentifier = $derived(organization?.slug ?? orgIdentifier)
  const organizationDisplayName = $derived(
    organization?.public_name?.trim() || organization?.name?.trim() || "dem Betrieb",
  )
  const organizationPrivacyUrl = $derived(
    organization?.data_privacy_url?.trim() ||
      `/presentation/${encodeURIComponent(effectiveOrgIdentifier)}/datenschutz`,
  )
  const hasCodeInUrl = $derived(Boolean(normalizePortalCode(initialCode)))

  const updateUrlCode = (nextCode: string | null) => {
    if (typeof window === "undefined") return

    const url = new URL(window.location.href)

    if (nextCode) {
      url.searchParams.set("code", nextCode)
    } else {
      url.searchParams.delete("code")
    }

    window.history.replaceState({}, "", url.toString())
  }

  type AppointmentActionParam = "book" | "reschedule" | "cancel" | "confirm" | "wrong_recipient"

  const appointmentActionParam = (): AppointmentActionParam | null => {
    if (typeof window === "undefined") return null

    const raw = new URL(window.location.href).searchParams.get("appointment_action")

    if (
      raw === "book" ||
      raw === "reschedule" ||
      raw === "cancel" ||
      raw === "confirm" ||
      raw === "wrong_recipient"
    ) {
      return raw
    }

    return null
  }

  const clearAppointmentActionParam = () => {
    if (typeof window === "undefined") return

    const url = new URL(window.location.href)

    if (!url.searchParams.has("appointment_action")) return

    url.searchParams.delete("appointment_action")
    window.history.replaceState({}, "", url.toString())
  }

  const loadState = async (nextCode: string) => {
    loading = true
    error = null
    successMessage = null
    try {
      portalState = await fetchAppointmentPortalState(effectiveOrgIdentifier, nextCode)
      loadedCode = nextCode
      portalView = "status"
      oncodeResolved?.(nextCode)
    } catch (err) {
      console.error("appointment_portal_state_load_failed", err)
      portalState = null
      loadedCode = null
      oncodeResolved?.(null)

      if (err instanceof ApiError && err.status === 404) {
        error = "Dieser Code wurde nicht gefunden."
      } else {
        error =
          (err as { message?: string })?.message ?? "Terminportal konnte nicht geladen werden."
      }
    } finally {
      loading = false
    }
  }

  const submitCode = (nextCode: string) => {
    const normalized = normalizePortalCode(nextCode)

    if (!normalized) return

    code = normalized
    updateUrlCode(normalized)
  }

  const resetCode = () => {
    clearContactCompletion()
    code = null
    loadedCode = null
    portalState = null
    error = null
    portalView = "status"
    successMessage = null
    handledAppointmentAction = null
    updateUrlCode(null)
    oncodeResolved?.(null)
  }

  const clearContactCompletion = () => {
    completionRevision += 1
    const overlayId = contactCompletionOverlayId
    contactCompletionOverlayId = null
    contactCompletionPrompt = null

    if (overlayId) closeOverlayById(overlayId)
  }

  const offerContactCompletion = async (
    response: PublicAppointmentPortalMutationResponse,
    participant: PublicAppointmentParticipantInput,
    targetOrg: string,
    targetCode: string,
  ) => {
    const appointment = response.state.appointment
    const availability = response.contact_completion

    if (destroyed || !appointment || response.state.state !== "confirmed" || !availability) return

    if (availability.email === "present" && availability.phone === "present") return

    const offerKey = `${targetOrg}:${targetCode}:${participant.idempotency_key}`

    if (offeredContactCompletions.has(offerKey)) return

    offeredContactCompletions.add(offerKey)
    const revision = ++completionRevision
    // Wait only for the action overlay to close; all facts came with the mutation.
    await tick()

    if (
      destroyed ||
      revision !== completionRevision ||
      targetOrg !== effectiveOrgIdentifier ||
      targetCode !== code ||
      portalView !== "status"
    )
      return

    contactCompletionPrompt = {
      orgIdentifier: targetOrg,
      code: targetCode,
      appointmentId: appointment.id,
      participant,
      availability,
    }
    const prompt = contactCompletionPrompt
    contactCompletionOverlayId = openOverlay({
      title: "Ihr Termin ist bestätigt",
      content: ContactCompletionDialog,
      isDark: false,
      defaultMobileFullscreen: true,
      properties: {
        availability,
        organizationDisplayName,
        organizationPrivacyUrl,
        onSave: (supplement: PublicAppointmentContactSupplement) =>
          saveContactCompletion(prompt, supplement),
      },
      onClose: () => {
        if (contactCompletionPrompt !== prompt) return

        completionRevision += 1
        contactCompletionPrompt = null
        contactCompletionOverlayId = null
      },
    })
  }

  const saveContactCompletion = async (
    prompt: ContactCompletionPrompt,
    supplement: PublicAppointmentContactSupplement,
  ) => {
    if (
      contactCompletionPrompt !== prompt ||
      effectiveOrgIdentifier !== prompt.orgIdentifier ||
      code !== prompt.code
    )
      return

    await supplementAppointmentContactDetails(
      prompt.orgIdentifier,
      prompt.code,
      prompt.appointmentId,
      supplement,
    )

    if (
      contactCompletionPrompt !== prompt ||
      effectiveOrgIdentifier !== prompt.orgIdentifier ||
      code !== prompt.code
    )
      return

    onContactMethodSubmitted?.({
      ...prompt.participant,
      email: supplement.email ?? prompt.participant.email,
      phone: supplement.phone ?? prompt.participant.phone,
    })
    newNotification({
      class: "success",
      html: "Vielen Dank! Ihre Kontaktdaten wurden an den Betrieb übermittelt.",
    })
  }

  $effect(() => {
    // Track both bearer scope inputs; cleanup also aborts pending reads on unmount.
    void effectiveOrgIdentifier
    void code

    return () => {
      clearContactCompletion()
      offeredContactCompletions.clear()
    }
  })
  onDestroy(() => {
    destroyed = true
    clearContactCompletion()
  })

  const applyMutationState = (
    responseState: PublicAppointmentPortalStateResponse,
    message: string,
  ) => {
    portalState = responseState
    successMessage = message
    portalView = "status"
    newNotification({
      class: "success",
      html: message,
    })
  }

  const showBookingError = (message: string) => {
    error = message
    newNotification({
      class: "error",
      html: message,
    })
  }

  const actionTitle = (action: "confirm" | "book" | "cancel") => {
    if (action === "cancel") return "Termin absagen"

    if (action === "book") return "Auswahl bestätigen"

    return "Termin bestätigen"
  }

  const openActionOverlay = (
    action: "confirm" | "book" | "cancel",
    slot: PublicAppointmentSlot | PublicAppointmentSummary | null = null,
    bookingSource: "recommendations" | "calendar" | null = null,
  ) => {
    error = null
    clearContactCompletion()
    const actionRevision = completionRevision
    const targetOrg = effectiveOrgIdentifier
    const targetCode = code
    let completed: {
      response: PublicAppointmentPortalMutationResponse
      participant: PublicAppointmentParticipantInput
    } | null = null
    openOverlay({
      title: actionTitle(action),
      content: AppointmentActionDetailsOverlay,
      defaultMobileFullscreen: true,
      properties: {
        action,
        organizationDisplayName,
        organizationPrivacyUrl,
        contactMethodHints,
        selectedSlot: slot,
        onsubmit: async (participant: PublicAppointmentParticipantInput) => {
          if (!targetCode || targetCode !== code || targetOrg !== effectiveOrgIdentifier)
            return false

          if (action === "confirm") {
            const response = await submitConfirmAppointment(participant)

            if (!response) return false

            completed = { response, participant }
          } else if (action === "book") {
            const response = await submitBookSlot(slot, participant, bookingSource)

            if (!response) return false

            completed = { response, participant }
          } else {
            await submitCancelAppointment(participant)
          }

          return true
        },
        oncompleted: () => {
          if (
            completed &&
            actionRevision === completionRevision &&
            targetCode &&
            targetCode === code &&
            targetOrg === effectiveOrgIdentifier
          ) {
            void offerContactCompletion(
              completed.response,
              completed.participant,
              targetOrg,
              targetCode,
            )
          }
        },
      },
    })
  }

  const confirmCurrentAppointment = () => {
    openActionOverlay("confirm", portalState?.appointment ?? null)
  }

  const submitConfirmAppointment = async (participant: PublicAppointmentParticipantInput) => {
    if (!code) return null

    const targetCode = code
    const targetOrg = effectiveOrgIdentifier

    busy = true
    error = null
    try {
      const response = await confirmAppointment(targetOrg, targetCode, participant)

      if (targetCode !== code || targetOrg !== effectiveOrgIdentifier) return null

      onContactMethodSubmitted?.(participant)
      applyMutationState(response.state, "Der Termin wurde bestätigt.")

      return response
    } catch (err) {
      console.error("appointment_confirm_failed", err)
      error = (err as { message?: string })?.message ?? "Termin konnte nicht bestätigt werden."

      return null
    } finally {
      busy = false
    }
  }

  const bookRecommendedSlot = async (slot: PublicAppointmentSlot): Promise<boolean> => {
    openActionOverlay("book", slot, "recommendations")

    return true
  }

  const bookCalendarSlot = async (slot: PublicAppointmentSlot): Promise<boolean> => {
    openActionOverlay("book", slot, "calendar")

    return true
  }

  const submitBookSlot = async (
    slot: PublicAppointmentSlot | null,
    participant: PublicAppointmentParticipantInput,
    bookingSource: "recommendations" | "calendar" | null,
  ) => {
    if (!code || !slot) return null

    const targetCode = code
    const targetOrg = effectiveOrgIdentifier

    busy = true
    error = null
    try {
      const response = await bookAppointmentSlot(targetOrg, targetCode, slot, participant)

      if (targetCode !== code || targetOrg !== effectiveOrgIdentifier) return null

      onContactMethodSubmitted?.(participant)
      applyMutationState(response.state, "Der neue Termin wurde gespeichert.")

      return response
    } catch (err) {
      console.error("appointment_book_failed", err)
      const conflictMessage = resolveSlotBookingConflictMessage(err)

      if (conflictMessage) {
        showBookingError(conflictMessage)

        if (bookingSource === "recommendations") {
          slotViewRevision += 1
          portalView = "recommendations"
        } else {
          portalView = "status"
        }
      } else {
        showBookingError(
          (err as { message?: string })?.message ?? "Termin konnte nicht gebucht werden.",
        )
      }

      return null
    } finally {
      busy = false
    }
  }

  const cancelCurrentAppointment = () => {
    openActionOverlay("cancel")
  }

  const submitCancelAppointment = async (participant: PublicAppointmentParticipantInput) => {
    if (!code) return

    busy = true
    error = null
    try {
      const response = await cancelAppointment(effectiveOrgIdentifier, code, participant)
      onContactMethodSubmitted?.(participant)
      applyMutationState(response.state, "Der Termin wurde abgesagt.")
    } catch (err) {
      console.error("appointment_cancel_failed", err)
      error = (err as { message?: string })?.message ?? "Termin konnte nicht abgesagt werden."
    } finally {
      busy = false
    }
  }

  const reportCurrentWrongRecipient = () => {
    error = null
    openOverlay({
      title: "Falscher Ansprechpartner",
      content: WrongRecipientReportOverlay,
      defaultMobileFullscreen: true,
      properties: {
        onsubmit: submitWrongRecipientReport,
      },
    })
  }

  const submitWrongRecipientReport = async (payload: PublicAppointmentWrongRecipientRequest) => {
    if (!code) return

    busy = true
    error = null
    try {
      const response = await reportWrongRecipient(effectiveOrgIdentifier, code, payload)
      applyMutationState(
        response.state,
        "Danke, wir haben den Hinweis an den Betrieb weitergegeben. Sie müssen nichts weiter tun.",
      )
    } catch (err) {
      console.error("appointment_wrong_recipient_failed", err)
      error = (err as { message?: string })?.message ?? "Der Hinweis konnte nicht gesendet werden."
    } finally {
      busy = false
    }
  }

  $effect(() => {
    const normalized = normalizePortalCode(initialCode)

    if (!normalized || normalized === code) return

    code = normalized
  })

  $effect(() => {
    if (!code || loadedCode === code || loading) return

    void loadState(code)
  })

  $effect(() => {
    if (!portalState || !code || loading || busy) return

    const action = appointmentActionParam()

    if (!action) return

    const actionKey = `${code}:${action}`

    if (handledAppointmentAction === actionKey) return

    handledAppointmentAction = actionKey

    if (
      (action === "book" || action === "reschedule") &&
      portalState.actions.includes("search_slots")
    ) {
      portalView = action === "reschedule" ? "recommendations" : "calendar"
    } else if (action === "confirm" && portalState.actions.includes("confirm")) {
      openActionOverlay("confirm", portalState.appointment)
    } else if (action === "cancel" && portalState.actions.includes("cancel")) {
      openActionOverlay("cancel")
    } else if (action === "wrong_recipient" && portalState.actions.includes("wrong_recipient")) {
      reportCurrentWrongRecipient()
    }

    clearAppointmentActionParam()
  })
</script>

{#if !code}
  <CodeGate {loading} {error} {presentationMode} {hasCodeInUrl} onsubmit={submitCode} />
{:else if loading && !portalState}
  <p>Terminportal wird geladen…</p>
{:else if !portalState}
  <CodeGate {loading} {error} {presentationMode} {hasCodeInUrl} onsubmit={submitCode} />
{:else}
  {#if successMessage}
    <p class="success">{successMessage}</p>
  {/if}
  {#if error}
    <p class="error">{error}</p>
  {/if}

  {#if portalView === "recommendations"}
    {#key slotViewRevision}
      <SlotRecommendationView
        orgIdentifier={effectiveOrgIdentifier}
        {code}
        {busy}
        onbook={bookRecommendedSlot}
        onback={() => (portalView = "status")}
        onfallbackcalendar={() => (portalView = "calendar")}
      />
    {/key}
  {:else if portalView === "calendar"}
    <SlotSelectionView
      orgIdentifier={effectiveOrgIdentifier}
      {code}
      {portalState}
      {busy}
      onbook={bookCalendarSlot}
      onback={() => (portalView = "status")}
    />
  {:else}
    <AppointmentStatusPanel
      {portalState}
      {busy}
      onconfirm={confirmCurrentAppointment}
      onchooseSlot={() => (portalView = portalState.appointment ? "recommendations" : "calendar")}
      oncancel={cancelCurrentAppointment}
      onwrongRecipient={reportCurrentWrongRecipient}
      onresetCode={resetCode}
    />
  {/if}
{/if}

<style>
  p,
  .success,
  .error {
    margin: 0;
  }

  .success,
  .error {
    padding: 0.8rem 1rem;
    font-weight: 700;
    margin-bottom: 1.2rem;
    border: 1px solid transparent;
  }

  .success {
    color: var(--primary-200);
    background: #fff;
    border-color: color-mix(in srgb, var(--primary-200) 28%, transparent);
  }

  .error {
    color: #cf3f38;
    background: rgba(207, 63, 56, 0.1);
    border-color: rgba(207, 63, 56, 0.28);
  }
</style>
